import React, { useState, useEffect, useMemo } from 'react';
import { Outlet, useNavigate, useLocation, useParams } from 'react-router-dom';
import Cookies from 'js-cookie';
import { 
  MdDashboard, 
  MdDescription, 
  MdSearch, 
  MdQrCodeScanner, 
  MdEvent,
  MdAnalytics,
  MdLibraryMusic,
  MdMic,
  MdMusicNote,
  MdAdminPanelSettings,
  MdLogout,
  MdMenu,
  MdClose
} from 'react-icons/md';
import { getActivityDetails } from '../../api/activities';
import Notification from '../../components/Notification';

// Theme configuration constants
const THEMES = {
  event: {
    name: 'Event Management',
    primary: 'blue',
    gradient: 'from-blue-600 to-indigo-600',
    background: 'bg-gray-50',
    sidebar: 'bg-white border-gray-200',
    text: 'text-gray-800',
    accent: 'text-blue-600'
  },
  fan: {
    name: 'Fan Management',
    primary: 'purple',
    gradient: 'from-purple-600 to-pink-600',
    background: 'bg-slate-50',
    sidebar: 'bg-white border-purple-200',
    text: 'text-slate-800',
    accent: 'text-purple-600'
  }
};

// Menu configuration
const getMenuItems = (activityId, activityType) => {
  const isEventType = !activityType || activityType === 'event';

  const items = [
    { 
      path: `/admin/${activityId}`, 
      label: 'Dashboard', 
      icon: isEventType ? MdDashboard : MdAnalytics
    },
    { 
      path: `/admin/${activityId}/forms`, 
      label: 'Form Management', 
      icon: isEventType ? MdDescription : MdLibraryMusic
    },
    { 
      path: `/admin/${activityId}/registrations`, 
      label: 'Registration Search', 
      icon: isEventType ? MdSearch : MdMic
    },
  ];

  items.push(
    { 
      path: `/admin/${activityId}/scanner`, 
      label: 'QR Scanner', 
      icon: MdQrCodeScanner
    },
    { 
      path: `/admin/activities`, 
      label: 'Activity Management', 
      icon: isEventType ? MdEvent : MdMusicNote
    }
  );

  return items;
};

const AdminLayout = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [error, setError] = useState(null);
  const [activityData, setActivityData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { activityId } = useParams();

  // Determine theme based on activity type
  const theme = useMemo(() => {
    const activityType = activityData?.type || 'event';
    return THEMES[activityType] || THEMES.event;
  }, [activityData]);

  const menuItems = useMemo(() => 
    getMenuItems(activityId, activityData?.type), 
    [activityId, activityData?.type]
  );

  useEffect(() => {
    const initializeLayout = async () => {
      const adminToken = Cookies.get('adminToken');
      
      if (!adminToken) {
        navigate(`/admin/${activityId}/login`);
        return;
      }

      try {
        setIsLoading(true);
        const response = await getActivityDetails(activityId);
        
        if (!response.success) {
          throw new Error(response.error || 'Failed to fetch activity details');
        }
        
        setActivityData(response.data);
      } catch (error) {
        console.error('Error fetching activity details:', error);
        Cookies.remove('adminToken');
        
        const errorMessage = error.response?.data?.error || 
                           error.message || 
                           'An unexpected error occurred';
        setError(errorMessage);
        
        setTimeout(() => {
          navigate(`/admin/${activityId}/login`);
        }, 3000);
      } finally {
        setIsLoading(false);
      }
    };

    if (activityId) {
      initializeLayout();
    }
  }, [activityId, navigate]);

  const handleLogout = () => {
    Cookies.remove('adminToken');
    navigate(`/admin/${activityId}/login`);
  };

  const handleNavigation = (path) => {
    navigate(path);
    setIsSidebarOpen(false);
  };

  const SidebarHeader = () => (
    <div className={`p-6 border-b ${theme.sidebar.replace('bg-white', 'border-gray-200')}`}>
      <div className="flex items-center space-x-3">
        <div className={`w-10 h-10 rounded-lg bg-gradient-to-r ${theme.gradient} flex items-center justify-center`}>
          <MdAdminPanelSettings className="text-white text-xl" />
        </div>
        <div>
          <h2 className={`text-xl font-semibold ${theme.text}`}>
            Admin Panel
          </h2>
          <p className={`text-sm ${theme.accent} font-medium`}>
            {theme.name}
          </p>
        </div>
      </div>
      {activityData && (
        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
          <p className="text-sm font-medium text-gray-600">Current Activity</p>
          <p className="text-sm text-gray-800 truncate">{activityData.name || 'Untitled Activity'}</p>
        </div>
      )}
    </div>
  );

  const SidebarNavigation = () => (
    <nav className="flex-1 px-4 py-6 space-y-2">
      {menuItems.map((item) => {
        const isActive = location.pathname === item.path;
        const IconComponent = item.icon;
        
        return (
          <button
            key={item.path}
            onClick={() => handleNavigation(item.path)}
            className={`w-full flex items-center px-4 py-3 text-left rounded-lg transition-all duration-200 ${
              isActive
                ? `bg-gradient-to-r ${theme.gradient} text-white shadow-sm`
                : `${theme.text} hover:bg-gray-100 hover:${theme.accent}`
            }`}
          >
            <IconComponent 
              className={`mr-3 text-lg ${isActive ? 'text-white' : theme.accent}`} 
            />
            <span className="font-medium">{item.label}</span>
          </button>
        );
      })}
    </nav>
  );

  const SidebarFooter = () => (
    <div className="p-4 border-t border-gray-200">
      <button
        onClick={handleLogout}
        className="w-full flex items-center justify-center px-4 py-3 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors duration-200 font-medium"
      >
        <MdLogout className="mr-2" />
        Logout
      </button>
    </div>
  );

  const HamburgerButton = () => (
    <button
      onClick={() => setIsSidebarOpen(!isSidebarOpen)}
      className={`fixed z-50 p-3 rounded-lg bg-white shadow-lg hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 ${
        isSidebarOpen ? 'left-[17rem] top-4' : 'left-4 top-4'
      }`}
      aria-label="Toggle sidebar"
      style={{ zIndex: 9999 }}
    >
      {isSidebarOpen ? (
        <MdClose className="text-gray-600 w-5 h-5" />
      ) : (
        <MdMenu className="text-gray-600 w-5 h-5" />
      )}
    </button>
  );

  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${theme.background}`}>
      {/* Error Notification */}
      {error && (
        <Notification
          type="error"
          message={error}
          onClose={() => setError(null)}
        />
      )}

      {/* Hamburger Button */}
      <HamburgerButton />

      {/* Sidebar Overlay */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-25 z-30 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed top-0 left-0 h-full w-64 ${theme.sidebar} shadow-xl transform transition-transform duration-300 ease-in-out flex flex-col ${
          isSidebarOpen ? 'translate-x-0 z-50' : '-translate-x-full z-40'
        }`}
      >
        <SidebarHeader />
        <SidebarNavigation />
        <SidebarFooter />
      </div>

      {/* Main Content */}
      <div className={`transition-all duration-300 ease-in-out ${
        isSidebarOpen ? 'lg:ml-64' : ''
      }`}>
        <main className="p-6 lg:p-8">
          <div className="max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;